/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {useNSFWMedia} from '@app/hooks/useNSFWMedia';
import styles from '@app/styles/NSFWBlur.module.css';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import React from 'react';
import type {ReactElement} from 'react';
import {useCallback} from 'react';

interface NSFWEmojiWrapperProps {
	nsfw: boolean;
	channelId: string | undefined;
	children: ReactElement<{className?: string}>;
}

export const NSFWEmojiWrapper = observer(({nsfw, channelId, children}: NSFWEmojiWrapperProps): ReactElement | null => {
	const {shouldBlur, shouldBlock, canReveal, reveal} = useNSFWMedia(nsfw, channelId);

	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			if (shouldBlur && canReveal) {
				e.preventDefault();
				e.stopPropagation();
				reveal();
			}
		},
		[shouldBlur, canReveal, reveal],
	);

	if (shouldBlock) {
		return null;
	}

	if (shouldBlur) {
		return (
			<span className={styles.nsfwBlurContainer} onClick={handleClick} role="button" tabIndex={0}>
				{React.cloneElement(children, {
					className: clsx(children.props.className, styles.nsfwBlurred),
				})}
			</span>
		);
	}

	return children;
});
